import { useState, useCallback } from 'react';
import useSWR, { preload } from 'swr';
import {
  listOrgMembersApiv2,
  updateOrgMemberApi,
  removeOrgMemberApi,
} from 'src/api/org-members';
import type { OrgMember } from '@equaliq/api-model-v2';

const fetcher = async (orgId: string) => {
  const response = await listOrgMembersApiv2({ orgId });
  return Object.values(response.members ?? {}) as OrgMember[];
};

export const preloadOrgMembers = (orgId: string) =>
  preload(['members', orgId], () => fetcher(orgId));

export const useOrgMembers = (orgId?: string) => {
  const swrKey = orgId ? ['members', orgId] : null;

  const { data, error, isLoading, mutate } = useSWR(swrKey, () => fetcher(orgId!));

  const members = data ?? [];

  // UI state — local useState per 004_state.txt
  const [editingMember, setEditingMember] = useState<OrgMember | null>(null);

  const refreshMembers = async () => {
    await mutate();
  };

  const updateMember = useCallback(async (
    userId: string,
    role?: string,
    customRoleId?: string
  ) => {
    await mutate(
      members.map(m =>
        m.userId === userId
          ? { ...m, role: role || m.role, customRoleId: customRoleId || m.customRoleId }
          : m
      ),
      false
    );

    try {
      await updateOrgMemberApi({ orgId: orgId!, userId, role, customRoleId });
      await mutate();
    } catch (err) {
      await mutate();
      throw err;
    }
    setEditingMember(null);
  }, [orgId, members, mutate]);

  const removeMember = useCallback(async (userId: string) => {
    await mutate(
      members.filter(m => m.userId !== userId),
      false
    );

    try {
      await removeOrgMemberApi({ orgId: orgId!, userId });
      await mutate();
    } catch (err) {
      await mutate();
      throw err;
    }
  }, [orgId, members, mutate]);

  const startEditingMember = useCallback((member: OrgMember) => {
    setEditingMember(member);
  }, []);

  const cancelEditingMember = useCallback(() => {
    setEditingMember(null);
  }, []);

  return {
    members,
    isLoading,
    error,
    editingMember,
    refreshMembers,
    updateMember,
    removeMember,
    startEditingMember,
    cancelEditingMember,
  };
};
